import React, { useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import DashboardLayout from '../../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import styles from './policyCancellation.module.scss';
import useFetchPolicyDetails from '../policyDetails/useFetchPolicyDetails';
import SoftSelect from '../../../components/SoftSelect';
import { policyCancellationReasons } from './const/policyCancellationReasons';
import { NotificationContext } from '../../../shared/context/notificationContext';
import {
  CancellationRejectCauses,
  checkForPolicyCancellationRejectCause,
} from '../policyDetails/policyDetails.helper';
import useCancelPolicy from './helpers/useCancelPolicy';
import useFetchPolicyRefund from './helpers/useFetchPolicyRefund';
import CancelledPolicyDetails from './cancelledPolicyDetails';
import PriceDiffSection from './PriceDiffSection/PriceDiffSection';
import SoftButton from 'components/SoftButton';
import Breadcrumbs from 'examples/Breadcrumbs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Close, ExpandMore } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { policyCancellationMethodReceived } from './const/policyCancellationMethodReceived';
import {
  policyCancellationDelayReasons,
  PolicyCancellationDelayReasonValue,
} from './const/policyCancellationDelayReasons';
import { LoaderContext } from 'shared/context/loaderContext';

const PolicyCancellationLayout = () => {
  const { t } = useTranslation();
  const { policyId } = useParams();
  const { showNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { policyDetails, fetchData } = useFetchPolicyDetails({
    policyId,
    persistentQuoteId: null,
    bookingDataId: null,
  });
  const { policyRefund, refundDate, fetchRefundData } = useFetchPolicyRefund();
  const { cancelPolicy } = useCancelPolicy();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cancellationRequestDate: null,
      methodReceived: null,
      delayReason: null,
      delayReasonOtherText: null,
      reason: null,
      confirmed: false,
    },
  });

  const { loading } = useContext(LoaderContext);

  const isCancelled = useMemo(
    () =>
      policyDetails?.policy?.status?.label === 'cancelled' ||
      policyDetails?.policy?.status.status === 13,
    [policyDetails],
  );

  const cancellationRequestDate = watch('cancellationRequestDate');
  const reason = watch('reason');
  const methodReceived = watch('methodReceived');
  const confirmed = watch('confirmed');
  const delayReason = watch('delayReason');
  const delayReasonOtherText = watch('delayReasonOtherText') || '';

  const minCancellationRequestDate = dayjs(
    policyDetails?.policy?.subscriptionDate,
  );
  const maxCancellationRequestDate = dayjs();

  const isCancellationRequestDateBeforeToday =
    cancellationRequestDate && cancellationRequestDate.isBefore(dayjs(), 'day');

  const handleDateChange = (date) => {
    if (!date) {
      return;
    }

    if (
      date.isBefore(minCancellationRequestDate) ||
      date.isAfter(maxCancellationRequestDate) ||
      !dayjs(date).isValid()
    ) {
      setError('cancellationRequestDate', {
        message: t('POLICY_CANCELLATION.validation.dateNotInRange'),
      });
      return;
    }
    clearErrors('cancellationRequestDate');
    setValue('cancellationRequestDate', date);
  };

  const handleReceivedMethodChange = (method) => {
    setValue('methodReceived', method);
  };

  const handleDelayReasonChange = (delayReason) => {
    setValue('delayReason', delayReason);
  };

  const handleReasonChange = (selectedReason) => {
    setValue('reason', selectedReason);
  };

  const handleConfirmChange = (event) => {
    setValue('confirmed', event.target.checked);
  };

  const handleDelayReasonOtherTextChange = (event) => {
    setValue('delayReasonOtherText', event.target.value);
  };

  const handleCancelPolicy = async (formData) => {
    const { cancellationRequestDate, methodReceived, delayReason, reason } =
      formData;

    const cancellationRejectCause =
      checkForPolicyCancellationRejectCause(policyDetails);
    if (
      cancellationRejectCause === CancellationRejectCauses.POLICY_ALREADY_ENDED
    ) {
      showNotification(
        t('POLICY_DETAILS.daysPolicyCancellationLimit'),
        'error',
      );
      return Promise.reject();
    }

    const shouldAddDelayReasonOtherText =
      isCancellationRequestDateBeforeToday && delayReason?.value === 'other';

    const formattedCancellationRequestDate = dayjs(
      cancellationRequestDate,
    ).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

    await cancelPolicy(
      policyDetails,
      policyRefund,
      refundDate,
      reason.value,
      formattedCancellationRequestDate,
      methodReceived.value,
      isCancellationRequestDateBeforeToday ? delayReason.value : null,
      shouldAddDelayReasonOtherText ? delayReasonOtherText : null,
    );
    await fetchData(policyId);
  };

  const navigateToPolicyDetails = () => {
    const queryParams = new URLSearchParams(location.search);
    const persistentQuoteId = queryParams.get('persistentQuoteId');
    const searchParams = new URLSearchParams();

    if (persistentQuoteId)
      searchParams.set('persistentQuoteId', persistentQuoteId);

    navigate(`/policy-details/${policyId}?${searchParams.toString()}`);
  };

  useEffect(() => {
    if (policyDetails) {
      fetchRefundData(policyDetails, dayjs().toDate());
    }
  }, [policyDetails]);

  const hasValidationErrors = Object.keys(errors).length > 0;

  const cancelPolicyButtonDisabled =
    loading ||
    hasValidationErrors ||
    !reason ||
    !confirmed ||
    !cancellationRequestDate ||
    !methodReceived ||
    (isCancellationRequestDateBeforeToday && !delayReason) ||
    (delayReason?.value === 'other' &&
      (!delayReasonOtherText || delayReasonOtherText.length < 5));

  if (!policyDetails) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar>
        <Breadcrumbs
          title={t('COMMON.cancel')}
          route={[
            { title: t('POLICIES.header'), link: '/policies' },
            {
              title: policyDetails?.policy.policyNumber,
              link: `/policy-details/${policyDetails?.policy.policyNumber}`,
            },
            {
              title: 'cancel',
            },
          ]}
        />
      </DashboardNavbar>
      <Box className={styles.PolicyCancellation}>
        {isCancelled ? (
          <CancelledPolicyDetails
            policyDetails={policyDetails}
            policyRefund={policyRefund}
          />
        ) : (
          <>
            <Typography variant="h2">
              {t('POLICY_CANCELLATION.cancelPolicy')}
            </Typography>
            <Typography
              variant="body2"
              className={styles.PolicyNumber}
              color="grey.700"
              sx={{ mb: 1 }}
            >
              {t('POLICY_CANCELLATION.policyNumber')}:{' '}
              <Typography
                variant="body2"
                component="span"
                color="gradients.info.main"
                fontWeight={700}
              >
                {policyDetails.policy.policyNumber}
              </Typography>
            </Typography>
            <Box className={styles.script}>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                {t('POLICY_CANCELLATION.script.title')}
              </Typography>
              <br />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: '0.85rem' }}
              >
                {t('POLICY_CANCELLATION.script.text')}
              </Typography>
            </Box>
            <form onSubmit={handleSubmit(handleCancelPolicy)}>
              <Box className={styles.select}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t('POLICY_CANCELLATION.label.date')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    required
                    minDate={minCancellationRequestDate}
                    maxDate={maxCancellationRequestDate}
                    format="DD-MM-YYYY"
                    sx={{ display: 'flex', maxWidth: '15rem' }}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        error: !!errors.cancellationRequestDate,
                        helperText:
                          errors.cancellationRequestDate?.message || '',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box className={styles.select}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t('POLICY_CANCELLATION.label.select')}
                </Typography>
                <SoftSelect
                  required
                  placeholder={t(
                    'POLICY_CANCELLATION.selectPlaceholder.select',
                  )}
                  options={policyCancellationMethodReceived}
                  onChange={handleReceivedMethodChange}
                  className="reason-dropdown"
                />
              </Box>

              {isCancellationRequestDateBeforeToday && (
                <>
                  <Box className={styles.select}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t('POLICY_CANCELLATION.label.delay')}
                    </Typography>
                    <SoftSelect
                      value={delayReason}
                      placeholder={t(
                        'POLICY_CANCELLATION.selectPlaceholder.reason',
                      )}
                      options={policyCancellationDelayReasons}
                      onChange={handleDelayReasonChange}
                    />
                  </Box>

                  {delayReason?.value ===
                    PolicyCancellationDelayReasonValue.OTHER && (
                    <Box className={styles.select}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.label.delayOther')}
                      </Typography>
                      <TextField
                        {...register('delayReasonOtherText')}
                        fullWidth
                        onChange={handleDelayReasonOtherTextChange}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 50, minLength: 3 }}
                        error={delayReasonOtherText.length < 3}
                        helperText={`${delayReasonOtherText.length}/50 characters`}
                        sx={{
                          '& .MuiInputBase-root': {
                            width: '100%',
                          },
                          '& .MuiInputBase-input': {
                            padding: 0,
                            lineHeight: 1.4,
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}

              <PriceDiffSection
                policyDetails={policyDetails}
                policyRefund={policyRefund}
                isCancelled={isCancelled}
              />

              {isCancellationRequestDateBeforeToday && (
                <Typography
                  variant="body2"
                  color="gradients.info.main"
                  sx={{ fontSize: '0.85rem', mb: 2, maxWidth: '30rem' }}
                >
                  {t('POLICY_CANCELLATION.info')}
                </Typography>
              )}

              <Box className={styles.reasonDropdown}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 700 }}>
                  {t('POLICY_CANCELLATION.label.cancellation')}
                </Typography>
                <SoftSelect
                  required
                  placeholder={t(
                    'POLICY_CANCELLATION.selectPlaceholder.reason',
                  )}
                  options={policyCancellationReasons}
                  value={getValues('reason')}
                  onChange={handleReasonChange}
                  className="reason-dropdown"
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register('confirmed')}
                    checked={confirmed}
                    onChange={handleConfirmChange}
                    name="confirmed"
                    color="primary"
                  />
                }
                label={
                  <Box className={styles.consentText}>
                    <Typography variant="body2" mb={0.5}>
                      {t('POLICY_CANCELLATION.consent.text')}
                    </Typography>
                    <List sx={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                      <ListItem sx={{ display: 'list-item', mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.consent.point1')}
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.consent.point2')}
                      </ListItem>
                    </List>
                    <Typography variant="body2" fontStyle="italic">
                      {t('POLICY_CANCELLATION.consent.disclaimer')}
                    </Typography>
                  </Box>
                }
                className={styles.consentCheckbox}
                sx={{
                  maxWidth: '40rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              />
              <Grid container spacing={2} className={styles.actions}>
                <Grid item>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigateToPolicyDetails()}
                    sx={{ '&:hover': { borderColor: '#0a76ec' } }}
                  >
                    {t('COMMON.back')}
                  </SoftButton>
                </Grid>
                <Grid item>
                  <SoftButton
                    type="submit"
                    variant="contained"
                    color="info"
                    disabled={cancelPolicyButtonDisabled}
                    sx={{ '&:hover': { borderColor: '#0a76ec' } }}
                  >
                    {t('POLICY_CANCELLATION.cancelPolicy')}
                  </SoftButton>
                </Grid>
              </Grid>
            </form>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default PolicyCancellationLayout;
